import { ChangeEvent, KeyboardEvent, useCallback, useRef, useState } from 'react';
import { InputBaseProps } from '@mui/material';
import { Input } from '@/UI/Input/Input';

interface TextInputProps extends Omit<
  InputBaseProps,
  'size' | 'ref' | 'onSubmit' | 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'inputRef'
> {
  value: string;
  onSubmit: (value: string) => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  onEnterAfterSubmit?: (value: string) => void;
  onEmptyBackspace?: () => void;
  onTabNavigate?: (direction: 'next' | 'prev') => void;
}

// TODO: this is only used in ArrayNode. Need to consolidate input lists in array, iterator and list nodes
export function TextInput({
  value,
  onSubmit,
  inputRef,
  onEnterAfterSubmit,
  onEmptyBackspace,
  onTabNavigate,
  ...restProps
}: TextInputProps) {
  const innerInputRef = useRef<HTMLInputElement | null>(null);
  const skipNextBlurSubmitRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState<string>();

  const handleSubmit = useCallback(
    (newValue: string | undefined) => {
      if (newValue !== undefined && newValue !== '') {
        onSubmit(newValue);
      } else {
        setLocalValue(value);
      }
    },
    [onSubmit, value],
  );

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === 'Tab' || evt.code === 'Tab') {
        evt.preventDefault();
        evt.stopPropagation();
        onTabNavigate?.(evt.shiftKey ? 'prev' : 'next');
        return;
      }

      if (evt.key === 'Enter' || evt.code === 'Enter') {
        evt.preventDefault();
        evt.stopPropagation();
        if (localValue === undefined || localValue === '') {
          setLocalValue(value);
        } else {
          if (onEnterAfterSubmit) {
            skipNextBlurSubmitRef.current = true;
            onEnterAfterSubmit(localValue);
            return;
          }
          handleSubmit(localValue);
        }
      } else if (evt.key === 'Escape' || evt.code === 'Escape') {
        // press Escape -> cancel edit
        setLocalValue(value);
      } else if (evt.code === 'z' && evt.getModifierState('Control')) {
        // stop Cmd+z propagation
        evt.stopPropagation();
        evt.preventDefault();
      } else if ((evt.key === 'Backspace' || evt.code === 'Backspace') && localValue === '') {
        evt.preventDefault();
        evt.stopPropagation();
        skipNextBlurSubmitRef.current = true;
        onEmptyBackspace?.();
      } else if (evt.code === 'Backspace' || evt.code === 'Delete') {
        evt.stopPropagation();
      }
    },
    [localValue, handleSubmit, onEmptyBackspace, onEnterAfterSubmit, onTabNavigate, value],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (skipNextBlurSubmitRef.current) {
      skipNextBlurSubmitRef.current = false;
      return;
    }
    if (localValue !== undefined) {
      handleSubmit(localValue);
    }
  }, [localValue, handleSubmit]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setLocalValue(value);
  }, [value]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const displayedValue = isFocused ? localValue : value;

  return (
    <Input
      className="nodrag"
      value={displayedValue}
      {...restProps}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      inputRef={(el: HTMLInputElement | null) => {
        innerInputRef.current = el;
        inputRef?.(el);
      }}
    />
  );
}
